import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { Head1, BodyText, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import {
    SHIFT_PROGRAM_STATUS_LABELS,
} from 'types/shiftPrograms.interface'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { useState } from 'react'
import { clearShiftPrograms, getShiftPrograms, pauseShiftProgram, resumeShiftProgram } from 'features/operations/shiftProgramsSlice'
import { IShiftProgramResponse } from 'types/api/operationsShiftProgramsApi.interface'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import useQueryPagination from 'hooks/useQueryPagination'
import { NoData } from 'components/NoData'
import { Pagination } from 'components/Pagination/Pagination'
import { OptionsEditIcon } from 'components/Options/Icons/OptionsEditIcon'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'

const getLastRunLabel = (lastRunStatus: 1 | 2 | 3) => {
    if (lastRunStatus === 2) return 'Missed'
    if (lastRunStatus === 3) return 'Partial'
    return 'Completed'
}

const getLastRunTone = (lastRunStatus: 1 | 2 | 3): 'success' | 'danger' | 'warning' => {
    if (lastRunStatus === 2) return 'danger'
    if (lastRunStatus === 3) return 'warning'
    return 'success'
}

const mapFrequencyType = (frequencyType?: string, recurrenceType?: 1 | 2 | 3 | 4) => {
    if (recurrenceType === 2) return 'DAILY'
    if (recurrenceType === 3) return 'WEEKLY'
    if (recurrenceType === 4) return 'CUSTOM'
    if (recurrenceType === 1) return 'EVERY SHIFT'
    if (frequencyType === 'DAILY') return 'DAILY'
    if (frequencyType === 'WEEKLY') return 'WEEKLY'
    if (frequencyType === 'CUSTOM') return 'CUSTOM'
    return 'EVERY SHIFT'
}

const columns: IHonestTableColumn<IShiftProgramResponse>[] = [
    {
        id: 'program',
        header: 'Program',
        fill: true,
        sticky: true,
        minWidth: '16rem',
        cell: (item) => (
            <Cell>
                <BodyText weight='600'>{item.name}</BodyText>
                <OtherText grey>
                    {item.site?.name ?? '-'} · {mapFrequencyType(item.frequencyType, item.recurrenceType)} · {item.shifts?.length ?? 0} shifts
                </OtherText>
            </Cell>
        ),
    },
    {
        id: 'frequency',
        header: 'Frequency',
        minWidth: '8rem',
        cell: (item) => (
            <StatusPill status='accent'>{mapFrequencyType(item.frequencyType, item.recurrenceType)}</StatusPill>

        ),
    },
    {
        id: 'lastRun',
        header: 'Last run',
        align: 'right',
        minWidth: '12rem',
        cell: (item) => (
            <RightCell>
                {item.lastRunAt && item.lastRunStatus ? (
                    <StatusPill status={getLastRunTone(item.lastRunStatus)}>
                        {getLastRunLabel(item.lastRunStatus)} · {formatDateForDisplay(item.lastRunAt, true)}
                    </StatusPill>
                ) : (
                    <OtherText grey>—</OtherText>
                )}
            </RightCell>
        ),
    },
    {
        id: 'nextRunAt',
        header: 'Next run',
        align: 'right',
        minWidth: '10rem',
        cell: (item) => (
            <RightCell>
                <OtherText grey>
                    {item.status === 2 ? '—' : item.nextRunAt ? formatDateForDisplay(item.nextRunAt, true) : '-'}
                </OtherText>
            </RightCell>
        ),
    },
    {
        id: 'status',
        header: 'Status',
        align: 'right',
        minWidth: '8rem',
        cell: (item) => {
            const status = item.status === 2 ? 'paused' : 'active'
            return (
                <RightCell>
                    <StatusPill status={status === 'active' ? 'success' : 'accent'}>{SHIFT_PROGRAM_STATUS_LABELS[status]}</StatusPill>
                </RightCell>
            )
        },
    },
]

export const ShiftPrograms = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const { data, totalPages } = useSelector((state: RootState) => state.shiftPrograms)
    const programs = data ?? []
    useQueryPagination({ fetchFunctions: [getShiftPrograms], cleanUpFunctions: [clearShiftPrograms], params })

    const getOptions = (item: IShiftProgramResponse): IHonestTableMenuOption<IShiftProgramResponse>[] => {
        const options: IHonestTableMenuOption<IShiftProgramResponse>[] = [
            { label: 'Edit', icon: <OptionsEditIcon />, onClick: (row) => navigate(`${row.id}/edit`) },
        ]

        if (item.status === 1) {
            options.push({ label: 'Pause', onClick: (row) => dispatch(pauseShiftProgram(String(row.id))) })
        } else if (item.status === 2) {
            options.push({ label: 'Resume', onClick: (row) => dispatch(resumeShiftProgram(String(row.id))) })
        }

        return options
    }

    return (
        <>
            <NavBar />
            <PageWrapper>
                <Flex between width='100%' mb='16'>
                    <Flex center gap='1rem'>
                        <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Shift Programs</Head1>
                    </Flex>
                    <Button variant='primary' onClick={() => navigate('add')}>Add Program</Button>
                </Flex>
                {programs.length ? (
                    <HonestTable
                        columns={columns}
                        data={programs}
                        keyField='id'
                        onClick={(item) => navigate(`${item.id}`)}
                        menuOptions={(item) => getOptions(item)}
                    />
                ) : (
                    <Flex column center justcenter gap='1rem'>
                        <NoData text='No shift programs configured. Set up recurring work for your teams.' />
                        <Button variant='primary' onClick={() => navigate('/operations/shift-programs/add')}>Create shift program</Button>
                    </Flex>
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
